
import React from "react";
import { i18nManager } from "../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, EarningService, IMember, ITeam, ITeamAnalytics, ITeamMember, ITeamMemberEarning, ITeamTopProgram, ITeamTransaction, SanitizedResponse, TeamRole, TeamService, TransactionFlow } from "../../../../services";
import { ChartData, ChartType, Classname, Column, DateHelper, DateTimeInput, DateTimePickerLayout, DateTimePickerSelectionMode, Dropdown, FormControl, StringHelper, Table } from "@rivtn/noseur";
import { Capability, Schemes, Utils } from "../../../../utils";
import { Elements } from "../../../shared/Elements";
import { ChartManager } from "../../../shared/ChartManager";
import AnalCard from "../../../shared/analytics/AnalCard";
import { findPlan, OyaGbewa, usePlan } from "@syllektis/oya-gbewa-react";
import { ChangePlan } from "../../account_settings/plan_and_billing/ManagePlan";

export interface TeamGeneralAnalyticsProps {
    profile: IMember;
}

function TeamGeneralAnalytics(props: TeamGeneralAnalyticsProps) {

    const { profile } = props;
    const teamService = TeamService.getInstance();
    const labels = i18nManager.Labels.teams.analytics;
    let { id: teamId = "" } = ReactRouterDom.useParams();
    const [team, setTeam] = React.useState<ITeam>({} as any);
    const teamAnalyticsChartDataState = React.useState<ChartData>();
    const [daysDifference, setDaysDifference] = React.useState<number>(0);
    const [topPrograms, setTopPrograms] = React.useState<ITeamTopProgram[]>();
    const [teamAnalytics, setTeamAnalytics] = React.useState<ITeamAnalytics>();
    const { teamReportingAndAnalytics } = usePlan({ teamReportingAndAnalytics: false });
    const [teamMemberEarnings, setTeamMemberEarnings] = React.useState<ITeamMemberEarning[]>();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [teamTransactions, setTeamTransactions] = React.useState<ApiResponseData<ITeamTransaction>>({});
    const currencySign = i18nManager.getCurrencySign(searchParams.get("curr") ?? "NGN");

    React.useEffect(() => {
        if (!teamReportingAndAnalytics) {
            findPlan(OyaGbewa.getPlan()!, { capability: Capability.TEAM_REPORTING_AND_ANALYTICS }, (r) => {
                ChangePlan.upSell({ profile: Utils.PROFILE, capability: Capability.TEAM_REPORTING_AND_ANALYTICS, planName: r.n, planLevel: r.l, cancelAction: () => window.history.back() });
            });
            return;
        }
        fetchInformation();
    }, []);

    React.useEffect(() => {
        setDaysDifference(DateHelper.getDaysBetweenDates(
            (searchParams.has("from") ? new Date(searchParams.get("from")!) : DateHelper.getStartOfMonth()),
            (searchParams.has("to") ? new Date(searchParams.get("to")!) : new Date())));
    }, [searchParams]);

    return (<div className="earnings analytics">
        <div className="header">
            <div className="l" style={{ display: "flex", flexDirection: "column" }}>
                <span className="t lined-limit"><i className="fa fa-arrow-left" onClick={() => window.history.back()} />
                    {i18nManager.Labels.teams.title + " - " + teamId + " - " + labels.title}
                </span>
                <span className="lined-limit" style={{ marginLeft: 25, fontWeight: "normal" }}>{team.name}</span>
            </div>
            <div className="queries">
                <Dropdown noIcon scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: labels.country_currency, }}
                    selectedOptionIndex={(i18nManager.CountriesAndCurrencies?.findIndex((p) => p?.currency_code === (searchParams.get("curr") ?? "NGN")) ?? 0)}
                    textInputProps={{ style: { paddingLeft: 15 } }}
                    options={i18nManager.CountriesAndCurrencies} optionMap={{
                        value: "currency_code",
                        label: "{label} ({currency_name} - {currency_code})",
                    }} onSelectOption={(option) => {
                        Utils.updateSearchParams("curr", option.currency_code, setSearchParams, fetchInformation);
                    }} />
                <FormControl className="date-range-dd" isFieldset scheme={Schemes.OMNEHOST} label={labels.date_range} rightContent={"fa fa-calendar"} style={{ width: 360 }}>
                    <DateTimeInput fill leftLayout={DateTimePickerLayout.FINE_LEFT_LAYOUT} formatDate={(date) => Utils.formatToFineDate4(date)}
                        selectedDates={[(searchParams.has("from") ? new Date(searchParams.get("from")!) : DateHelper.getStartOfMonth()), new Date(searchParams.get("to") ?? Utils.formatToApiDate(new Date(), false, false)!)]}
                        selectionMode={DateTimePickerSelectionMode.RANGE} onSelectDate={(options) => {
                            Utils.updateSearchParamses({
                                "to": Utils.formatToApiDate(options.toDate!, false, false),
                                "from": Utils.formatToApiDate(options.fromDate!, false, false),
                            }, setSearchParams, fetchInformation);
                        }} />
                </FormControl>
            </div>
        </div>
        <div className="container">
            <div className="primary">
                <div className="anal-cards">
                    <AnalCard anal={teamAnalytics?.earnings?.total} currencySign={currencySign} desc={labels.earned_within_x_days} title={labels.total_earnings}
                        icon="fas fa-sack-dollar" daysDifference={daysDifference} to={{ pathname: `./earnings`, search: window.location.search }} />
                    <AnalCard anal={teamAnalytics?.tournaments?.entered} desc={labels.tournaments_entered} title={labels.tournaments}
                        icon="fas fa-basketball-ball" analKey="count" daysDifference={daysDifference} to={{ pathname: `./tournaments`, search: window.location.search }} cardColor={"#EBFEF6"} />
                    <AnalCard anal={teamAnalytics?.achievements?.total} desc={labels.achievements_within_x_days} title={labels.achievements}
                        icon="fa fa-trophy" analKey="count" daysDifference={daysDifference} to={{ pathname: `./achievements`, search: window.location.search }} cardColor={"#EBF4FE"} />
                </div>
                {<ChartManager type={ChartType.BAR} title={labels.team_anals} chartDataState={teamAnalyticsChartDataState} />}
                <div className="anal-table">
                    <div className="header">
                        <span className="t">{labels.earnings.transactions.last_10_earning}</span>
                        <ReactRouterDom.NavLink to={{ pathname: `/teams/${teamId}/analytics/transactions`, search: window.location.search }}>{labels.earnings.transactions.view_all_trans}</ReactRouterDom.NavLink>
                    </div>
                    <Table stripedRows scheme={Schemes.OMNEHOST} data={teamTransactions.content} totalRecords={teamTransactions.total_elements} dataRefreshKeys={["external_id", "created_at"]}
                        style={{ flex: 1 }} rowsPerPage={200} loadingState={Elements.LOADING_STATE} emptyState={Elements.buildEmptyListView(undefined, (<span>
                            {labels.earnings.transactions.empty_label_parts.no_earning_found}{" "}
                            <ReactRouterDom.NavLink to="/tournaments">{labels.earnings.transactions.empty_label_parts.participate}</ReactRouterDom.NavLink>{" "}
                            {labels.earnings.transactions.empty_label_parts.in_tournament_or}
                            <ReactRouterDom.NavLink to={`/teams/${teamId}/merch`}>{labels.earnings.transactions.empty_label_parts.create_merch_store}</ReactRouterDom.NavLink>{" "}
                            {labels.earnings.transactions.empty_label_parts.to_generate_incomes}
                        </span>), { marginTop: 50 })}>
                        <Column className="table-c" header={labels.earnings.transactions.table.headers.date} dataKey="date" template={(v) => Utils.formatToFineDate(v, true)} />
                        <Column className="table-c" header={labels.earnings.transactions.table.headers.amount} dataKey="amount" template={(v: number) => (`${currencySign} ${StringHelper.toPresentableMoneyValue(v)}`)} />
                        <Column className="table-c" header={labels.earnings.transactions.table.headers.source} template={(v: ITeamTransaction) => {
                            return "TODO";
                            /*let link = EarningService.buildTransactionSourceLink(v);
                            const label = v.source?.label ?? v.source?.extra_data?.name;
                            return (<div style={{ display: "flex", flexDirection: "row", alignItems: "center", }}>
                                {v.source?.extra_data?.logo && (<img alt={label} src={v.source?.extra_data?.logo} style={{ width: 20, height: 20, marginRight: 10, borderRadius: "100%" }} />)}
                                {link ? (<a target="_blank" href={link}>{label}</a>) : (<span>{label}</span>)}
                            </div>);*/
                        }} />
                        <Column className="table-c" header={labels.earnings.transactions.table.headers.recipient} template={(v: ITeamTransaction) => ((v.recipient as ITeamMember).display_name)} />
                    </Table>
                </div>
            </div>
            <div className="secondary">
                <div className="tt-earners">
                    <span className="t">{labels.top_team_earners}</span>
                    {!teamMemberEarnings && Elements.LOADING_STATE}
                    <div className="listers">
                        {teamMemberEarnings?.map((teamMemberEarning) => {
                            const isOwner = teamMemberEarning.team_member.role === TeamRole.OWNER; // go to the earning filter by member
                            const isYou = teamMemberEarning.team_member.member?.external_id === profile.external_id && (labels.earnings.you);

                            return (<div key={teamMemberEarning.team_member.external_id} className="lister">
                                <img className="bg" alt={teamMemberEarning.team_member.display_name} src={teamMemberEarning.team_member.member?.profile_banner_url} />
                                <img className="lo" alt={teamMemberEarning.team_member.display_name} src={teamMemberEarning.team_member.passport_picture_url} />
                                <div className="details">
                                    <span className="m">{currencySign} {StringHelper.toPresentableMoneyValue(teamMemberEarning.amount)}</span>
                                    <span>
                                        {teamMemberEarning.team_member.display_name}{" "}
                                        {(isYou || isOwner) && `(${isOwner ? labels.earnings.owner + (isYou ? ", " : "") : ""}${isYou ? labels.earnings.you : ""})`}
                                    </span>
                                </div>
                                <i className="fa fa-angle-right" />
                            </div>);
                        })}
                    </div>
                </div>
                <div className="tt-programs">
                    <span className="t">{labels.top_programs}</span>
                    {!topPrograms && Elements.LOADING_STATE}
                    {(topPrograms && !topPrograms.length) && (Elements.buildEmptyListView(undefined, (<>
                        <span>{labels.no_programs_parts.no_pros_found} <ReactRouterDom.NavLink to={"/tournaments"}>{labels.no_programs_parts.participate}</ReactRouterDom.NavLink> {labels.no_programs_parts.in_tournament_to}</span>
                    </>), { marginBottom: 100 }))}
                    <div className="listers">
                        {topPrograms?.map((topProgram) => (<div key={topProgram.program.external_id} className="lister">
                            <img alt={topProgram.program.name} src={topProgram.program.logo_urls?.[0]} />
                            <span className="n">{topProgram.program.name}</span>
                            <span className="l">{topProgram.count} {labels.tournament_s}</span>
                        </div>))}
                    </div>
                </div>
            </div>
        </div>
    </div>);

    function fetchInformation() {
        teamService.getTeamByPublicId(teamId!).then(({ sanitized }: SanitizedResponse<ITeam>) => {
            setTeam(sanitized);
        }).catch(BaseService.reportError);

        const params = {
            currency_code: searchParams.get("curr") ?? "NGN",
            to: searchParams.get("to") ?? Utils.formatToApiDate(new Date())!,
            fields: ["earnings.total", "tournaments.entered", "achievements.total"],
            from: searchParams.get("from") ?? Utils.formatToApiDate(DateHelper.getStartOfMonth())!,
        };
        setTeamAnalytics(undefined);
        teamService.getTeamAnalytics(teamId, params).then(({ sanitized }: SanitizedResponse<ITeamAnalytics>) => {
            setTeamAnalytics(sanitized);
        }).catch(BaseService.reportError);

        setTeamMemberEarnings(undefined);
        teamService.getTeamMemberEarnings(teamId, params).then(({ content }: SanitizedResponse<ITeamMemberEarning>) => {
            setTeamMemberEarnings(content);
        }).catch(BaseService.reportError);

        setTopPrograms(undefined);
        teamService.getTeamTopPrograms(teamId, params).then(({ content }: SanitizedResponse<ITeamTopProgram>) => {
            setTopPrograms(content);
        }).catch(BaseService.reportError);

        teamAnalyticsChartDataState[1](undefined);
        teamService.queryAnalyticsChartData(teamId, params).then(({ apiResponse }: SanitizedResponse<any>) => {
            teamAnalyticsChartDataState[1](apiResponse.data!);
        }).catch(BaseService.reportError);

        setTeamTransactions({});
        teamService.queryTransactions(teamId, { ...params, size: 10, flow: TransactionFlow.OUT }).then(({ apiResponse }: SanitizedResponse<any>) => {
            setTeamTransactions(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

}

export default TeamGeneralAnalytics;
